import React, { FC, useMemo, Fragment, useState, useRef, useEffect } from 'react'

import InfoIcon from '@pixel/assets/icons/Info'

import AutoSelect from '@pixel/core/AutoSelect';
import CircularProgress from '@pixel/core/CircularProgress'
import ContentModal from '@pixel/core/ContentModal'
import FileUpload from '@pixel/core/FileUpload/FileUpload';
import FormControl from '@pixel/core/FormControl'
import FormInputLayout from '@pixel/core/FormInputLayout'
import MenuItem from '@pixel/core/MenuItem'
import Stack from '@pixel/core/Stack'
import TextField from '@pixel/core/TextField'
import ToolTip from '@pixel/core/Tooltip'
import Typography from '@pixel/core/Typography'
import { useSnackbar } from '@pixel/core/SnackbarService'

import HTMLEditor from '@pixel/internal/Editor/HTMLEditor'

import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { useJHireURL } from '../../contexts/Domain'
import formatPluralString from "../../utils/formatPluralString";
import { useLocaleType, useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks'
import { useValidatedCandidatesList } from './ChangeStatusModal'
import { useJobDetailsEId } from '../../contexts/JobDetails';
import LocalizedTypography from '../LocalizedTypography';
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes';
import { Link } from '@mui/material';
import convertLineBreaksToBR, { removeLineBreaks } from '../../utils/formatEmailBody';
import FloatingCircularProgress from './FloatingCircularProgress';

const accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'application/pdf': ['.pdf'],
    'application/dmg': ['.dmg'],
    'application/doc': ['.doc'],
}

interface UserResponse {
    EId: string
    fullName: string
}

interface TemplateResponse {
    EId: string
    body: string
    subject: string
    title: string
    locale: string
    isDefault: boolean
}

type TemplateState = TemplateResponse & {
    group: string
}

interface SubmitToHiringManagerResponse {
    isGeneralApplication: boolean
    primaryHMEId: string
    templates: {
        [key: string]: Array<TemplateResponse>
    }
    users: Array<UserResponse>
}

interface SubmitToHMProps {
    selectedStatusEId: string
    open: boolean
    onClose: () => void
}
const SubmitToHM: FC<SubmitToHMProps> = ({
    selectedStatusEId,
    open,
    onClose,
}) => {
    const jhireURL = useJHireURL()
    const userLocalePreference = useLocaleType()
    const requisitionId = useJobDetailsEId()
    const snackbar = useSnackbar()

    const {
        COMMON_TO,
        COMMON_TEMPLATE,
        CANCEL,
        COMMON_MESSAGE_SEND,
        COMMON_SUBJECT_IS_REQUIRED,
        COMMON_CANDIDATE,
        COMMON_CANDIDATES,
        COMMON_FILE_UPLOAD,
        COMMON_FILE_DROP,
        COMMON_SUBJECT,
        JV_APPLICANT_CANDIDATE_STATUS_UPDATED_SUCCESSFULLY,
        JV_SENDHIRINGMANAGER_CONTROLLER_MESSAGE_SENT_ERROR,
        JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD,
        APPLICANT_SUBMITTOHIRINGMANAGER_SUBMIT_CANDIDATE_TO_HIRING_MANAGER,
        ADD_HIRING_MANAGER,
        COMMON_UPDATE_STATUS,
    } = useMultipleLocalizedText()

    const {
        validatedCandidates,
        validatedCandidateIds,
    } = useValidatedCandidatesList()

    const [toEmployee, setToEmployee] = useState<UserResponse>(null)
    const [templates, setTemplates] = useState([])
    const [template, setTemplate] = useState<TemplateState>(null)
    const [subject, setSubject] = useState('')
    const [files, setFiles] = useState([])
    const [toError, setToError] = useState('')
    const [templateError, setTemplateError] = useState('')
    const [subjectError, setSubjectError] = useState('')
    const [contentError, setContentError] = useState('')
    const bodyEditorRef = useRef(null)
    const templateRef = useRef(null)
    templateRef.current = template

    const submitToHM = useMutation({
        mutationKey: ['submitHiringManagerAction'],
        mutationFn: (payload: any) => axios.post(`${jhireURL}/action/application/list/${selectedStatusEId}/submitHM?readMode=0`, payload),
    })

    const handleResolve = async (reason) => {
        setToError('')
        setTemplateError('')
        setSubjectError('')
        setContentError('')

        if (data?.users?.length > 0) {
            let hasError = false
            if (!toEmployee) {
                hasError = true
                setToError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
            }

            if (!template) {
                hasError = true
                setTemplateError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
            }

            if (subject === '') {
                hasError = true
                setSubjectError(COMMON_SUBJECT_IS_REQUIRED)
            }

            if (bodyEditorRef.current.getValue() === '') {
                hasError = true
                setContentError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
            }

            if (hasError) {
                return true
            }
        }

        const noHMObject = {
            toList: [] as any[],
            entityIdList: validatedCandidateIds,
            subject: '',
            content: '',
        }

        let payloadObject = null

        switch(reason) {
            case 'add_hm':
                window.location.href = `/jhire/modules/requisitions/tabs.html#/details-edit/${requisitionId}`
                break
            case 'update_status':
                payloadObject = noHMObject
            case 'submit':
                if (!payloadObject) {
                    payloadObject = {
                        toList: [toEmployee.EId],
                        attachmentList: files.map(file => file.response.resourceInfo.eid),
                        entityIdList: validatedCandidateIds,
                        locale: template.locale,
                        subject,
                        content: removeLineBreaks(bodyEditorRef.current.getValue()),
                    }
                }
                try {
                    const { data } = await submitToHM.mutateAsync(reason === 'update_status' ? noHMObject : payloadObject)
                    if (data.msgInfo === 200) {
                        snackbar.showSuccess(JV_APPLICANT_CANDIDATE_STATUS_UPDATED_SUCCESSFULLY)
                        onClose()
                    }
                } catch(e) {
                    snackbar.showError(JV_SENDHIRINGMANAGER_CONTROLLER_MESSAGE_SENT_ERROR)
                }
                break
        }
    }

    const handleReject = () => {
        onClose()
    }

    const {
        data,
        status,
    } = useQuery({
        queryKey: ['submitToHiringManager', requisitionId, ...validatedCandidateIds],
        queryFn: () => {
            const params = new URLSearchParams({
                "applicationEIds": validatedCandidateIds && Array.isArray(validatedCandidateIds) ? validatedCandidateIds.join(',') : ''
            }).toString()

            return axios.get<SubmitToHiringManagerResponse>(`${jhireURL}/action/application/template/${requisitionId}/submitToHM?${params}`)
                .then(data => data.data)
        }
    })

    const handleHTMLEditorLoad = () => {
        setTimeout(() => {
            if (bodyEditorRef.current && templateRef.current) {
                bodyEditorRef.current.setValue(convertLineBreaksToBR(templateRef.current.body))
            }
        }, 10)
    }

    const validNamesList = useMemo(() => {
        if (validatedCandidates.length > 0) {
            return validatedCandidates.map(candidate => <Fragment key={candidate.EId}>{candidate.candidate.fullName} <br /></Fragment>)
        }

        return ''
    }, [validatedCandidates])

    useEffect(() => {
        const userLocaleTemplates = templates?.filter(template => template.locale.toLowerCase() === userLocalePreference.toLowerCase()) || []
        const defaultTemplate = userLocaleTemplates.find(template => template.isDefault)
        if (defaultTemplate) {
            setTemplate(defaultTemplate)
        } else if (templates.length > 0) {
            setTemplate(templates[0])
        } else {
            setTemplate(null)
        }
    }, [templates])

    useEffect(() => {
        if (template) {
            setSubject(template.subject)
            if (bodyEditorRef.current) {
                bodyEditorRef.current.setValue(convertLineBreaksToBR(template.body))
            }
        }
    }, [template])

    useEffect(() => {
        if (data && status === 'success') {
            setToEmployee(data.users.find(user => user.EId === data.primaryHMEId) ?? data.users[0])

            const templates: Array<TemplateState> = []
            Object.keys(data.templates)
                .forEach(key => data.templates[key]
                .forEach(template => templates.push({ ...template, group: key })))

            templates.sort((a, b) => -b.group.localeCompare(a.group))
            const userLocaleTemplates = templates?.filter(template => template.locale.toLowerCase() === userLocalePreference.toLowerCase()) || []
            const defaultTemplate = userLocaleTemplates.find(template => template.isDefault)
            setTemplate(defaultTemplate)
            setTemplates(templates)
        }
    }, [data, status])

    const modalActions = useMemo(() => {
        if (data?.users?.length === 0) {
            return [
                { reason: 'add_hm', label: <Link href={`/jhire/modules/requisitions/tabs.html#/details-edit/${requisitionId}`} color="#fff" underline="none" sx={{ '&:hover': { color: 'white' } }}>{ADD_HIRING_MANAGER}</Link> },
                { reason: 'update_status', label: COMMON_UPDATE_STATUS },
            ]
        } else if (data?.users?.length > 0) {
            return [{ reason: "submit", label: COMMON_MESSAGE_SEND }]
        }
    }, [data])

    return (
        <ContentModal
            open={open}
            showClose={true}
            onClose={onClose}
            sizeVariant="md"
            title={APPLICANT_SUBMITTOHIRINGMANAGER_SUBMIT_CANDIDATE_TO_HIRING_MANAGER}
            labelCloseAria="close"
            rejectActions={[{ reason: "cancel", label: CANCEL }]}
            resolveActions={modalActions}
            onResolve={handleResolve}
            onReject={handleReject}
            modalProps={{
                disableAutoFocus: true,
                disableEnforceFocus: true,
            }}
        >
            <FloatingCircularProgress open={submitToHM.isLoading} />
            {status === 'loading' && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            {status === 'error' && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <LocalizedTypography color="error" text={LocalizedText.JV_SENDHIRINGMANAGER_CONTROLLER_MESSAGE_GET_ERROR} />
                </div>
            )}
            {status === 'success' && data?.users?.length > 0 && (
                <Stack>
                    <div style={{ marginBottom: '16px' }}>
                        {validatedCandidates.length > 0 && (
                            <ToolTip title={<>{validNamesList}</>} placement="bottom-start">
                                <div>
                                    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                                        {validNamesList.length} {' '}
                                        {formatPluralString(
                                            validNamesList.length,
                                            COMMON_CANDIDATE,
                                            COMMON_CANDIDATES,
                                        )}
                                        <InfoIcon sx={{ marginLeft: '5px' }} />
                                    </Typography>
                                </div>
                            </ToolTip>
                        )}
                    </div>
                    <FormControl>
                        <AutoSelect
                            required
                            label={COMMON_TO}
                            value={toEmployee}
                            options={data.users}
                            onChange={(e, value: UserResponse) => setToEmployee(value)}
                            getOptionLabel={(option: UserResponse) => option.fullName}
                            isOptionEqualToValue={(option: UserResponse, value: UserResponse) => option?.EId === value?.EId}
                            errorText={toError}
                            error={toError !== ''}
                        />
                    </FormControl>
                    <FormControl>
                        <AutoSelect
                            required
                            label={COMMON_TEMPLATE}
                            value={template}
                            options={templates}
                            onChange={(e, value: TemplateState) => setTemplate(value)}
                            getOptionLabel={(option: TemplateState) => option.title}
                            isOptionEqualToValue={(option: TemplateState, value: TemplateState) => option?.EId === value?.EId}
                            errorText={templateError}
                            error={templateError !== ''}
                        />
                    </FormControl>
                    <FormInputLayout gridLayout="calc(100%)">
                        <TextField
                            required
                            label={COMMON_SUBJECT}
                            fullWidth
                            margin="normal"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            errorText={subjectError}
                            error={subjectError !== ''}
                        />
                    </FormInputLayout>
                    <HTMLEditor
                        ref={bodyEditorRef}
                        variant="standard"
                        enterMode="br"
                        mergeFields={{}}
                        disabled={false}
                        onLoad={handleHTMLEditorLoad}
                    />
                    {contentError && <Typography sx={{ color: 'red' }}>{JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD}</Typography>}
                    <div style={{ marginTop: '24px' }} />
                    <FileUpload
                        btnMultiLabel={COMMON_FILE_UPLOAD}
                        dragMultiLabel={COMMON_FILE_DROP}
                        maxFiles={4}
                        accept={accept}
                        multiple
                        uploadUrl={`${jhireURL}/action/resource/attachment?&getDownloadToken=true`}
                        onChange={(files) => setFiles(files)}
                    />
                </Stack>
            )}
            {status === 'success' && data?.users?.length === 0 && (
                <Stack>
                    <LocalizedTypography color="error" text={LocalizedText.CANDIDATE_MSG_NO_HIRING_MANAGER} />
                    <LocalizedTypography text={LocalizedText.CANDIDATE_MSG_NO_HM_ACTION_LECTURE1} />
                    <LocalizedTypography text={LocalizedText.CANDIDATE_MSG_NO_HM_ACTION_LECTURE2} />
                </Stack>
            )}
        </ContentModal>
    )
}

export default SubmitToHM
